import { useState, useEffect, useCallback } from 'react';
import { Box, Flex, Text, chakra } from '@chakra-ui/react';
import { X, MapPin, Search, TrendingUp, Calendar } from 'lucide-react';
import type { Location, LocationPickerProps, OpportunityLocationData } from './types';
import { getTopOpportunityLocations, getDescriptionFromSuggestion, addLocationIfNew, isGoogleMapsLoaded } from './utils';
import { useLocationPickerMap } from './useLocationPickerMap';

export type { Location } from './types';

export function LocationPicker({
  isOpen,
  onClose,
  onSelect,
  selectedLocations: initialSelectedLocations = [],
  events = [],
}: LocationPickerProps) {
  const [selectedLocations, setSelectedLocations] = useState<Location[]>(initialSelectedLocations);
  const [searchQuery, setSearchQuery] = useState('');
  const [globalSearchResults, setGlobalSearchResults] = useState<unknown[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  const mapRef = useLocationPickerMap(isOpen, selectedLocations);

  const topOpportunityLocations = getTopOpportunityLocations(events ?? [], 6);

  const searchGooglePlaces = useCallback(async (query: string) => {
    if (!query.trim() || query.length < 3) {
      setGlobalSearchResults([]);
      return;
    }
    if (!isGoogleMapsLoaded()) return;
    setIsSearching(true);
    try {
      const winG = (window as unknown as { google: { maps: { importLibrary: (lib: string) => Promise<unknown> } } }).google;
      const placesLib = await winG.maps.importLibrary('places') as { AutocompleteSuggestion: { fetchAutocompleteSuggestions: (r: object) => Promise<{ suggestions: unknown[] }> } };
      const response = await placesLib.AutocompleteSuggestion.fetchAutocompleteSuggestions({ input: query, includedPrimaryTypes: ['locality'] });
      setGlobalSearchResults(response?.suggestions ?? []);
    } catch {
      setGlobalSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => searchGooglePlaces(searchQuery), 500);
    return () => clearTimeout(t);
  }, [searchQuery, searchGooglePlaces]);

  useEffect(() => {
    if (isOpen) setSelectedLocations(initialSelectedLocations ?? []);
  }, [isOpen, initialSelectedLocations]);

  const handleGlobalLocationSelect = useCallback(async (placeId: string, description: string) => {
    if (!isGoogleMapsLoaded() || !placeId || placeId.startsWith('result-') || placeId.startsWith('location-')) return;
    try {
      const winG = (window as unknown as { google: { maps: { importLibrary: (lib: string) => Promise<unknown> } } }).google;
      const placesLib = await winG.maps.importLibrary('places') as { Place: new (o: { id: string }) => { fetchFields: (o: { fields: string[] }) => Promise<void>; location?: { lat: number | (() => number); lng: number | (() => number) }; formattedAddress?: string } };
      const place = new placesLib.Place({ id: placeId });
      await place.fetchFields({ fields: ['location', 'displayName', 'formattedAddress'] });
      if (!place.location) return;
      const lat = typeof place.location.lat === 'function' ? place.location.lat() : place.location.lat;
      const lng = typeof place.location.lng === 'function' ? place.location.lng() : place.location.lng;
      const region = description.split(',').slice(1).join(',').trim() || (place.formattedAddress || '').split(',').slice(1).join(',').trim();
      const newLocation: Location = { name: description.split(',')[0], latitude: lat, longitude: lng, region };
      setSelectedLocations((prev) => addLocationIfNew(prev, newLocation));
      setSearchQuery('');
      setGlobalSearchResults([]);
    } catch {
      // silent
    }
  }, []);

  const handleOpportunityLocationSelect = useCallback((location: OpportunityLocationData) => {
    const newLocation: Location = {
      name: location.name,
      latitude: location.latitude,
      longitude: location.longitude,
      region: location.region,
      activityCount: location.opportunityCount,
    };
    setSelectedLocations((prev) => addLocationIfNew(prev, newLocation));
  }, []);

  const removeSelectedLocation = useCallback((loc: Location) => {
    setSelectedLocations((prev) => prev.filter((l) => l.name !== loc.name));
  }, []);

  const handleConfirm = useCallback(() => {
    onSelect(selectedLocations);
    onClose();
  }, [onSelect, onClose, selectedLocations]);

  if (!isOpen) return null;

  return (
    <Flex
      position="fixed"
      inset="0"
      bg="rgba(0,0,0,0.5)"
      alignItems={{ base: 'flex-end', md: 'center' }}
      justifyContent="center"
      zIndex={1001}
      p={{ md: '4' }}
      onClick={onClose}
    >
      <Flex
        bg="white"
        borderTopRadius={{ base: '3xl', md: '2xl' }}
        borderBottomRadius={{ md: '2xl' }}
        w="full"
        maxH="90vh"
        minH={{ base: '80vh', md: '0' }}
        maxW={{ md: '4xl' }}
        flexDir="column"
        css={{
          '@media (max-width: 425px)': { minH: '85vh' },
          animation: { base: 'slideUpFromBottom 0.3s ease-out', md: 'none' },
        }}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        {/* Header */}
        <Flex
          alignItems="center"
          justifyContent="space-between"
          px={{ base: '5', sm: '4' }}
          py="4"
          borderBottom="1px solid"
          borderColor="volyx.borderLight"
          flexShrink={0}
        >
          <chakra.button
            p="2"
            borderRadius="full"
            bg="volyx.lightGray"
            border="none"
            cursor="pointer"
            display="flex"
            alignItems="center"
            justifyContent="center"
            color="volyx.text"
            transition="opacity 0.2s"
            _hover={{ opacity: 0.7 }}
            onClick={onClose}
          >
            <X size={24} />
          </chakra.button>
          <Text as="h3" m="0" fontSize="lg" fontWeight="semibold" color="volyx.text" fontFamily="body">
            Choose Locations
          </Text>
          <chakra.button
            bg="volyx.primary"
            py="2"
            px="5"
            borderRadius="full"
            border="none"
            cursor="pointer"
            color="white"
            fontFamily="body"
            fontWeight="medium"
            fontSize="md"
            transition="opacity 0.2s"
            _hover={{ opacity: 0.9 }}
            onClick={handleConfirm}
          >
            Done
          </chakra.button>
        </Flex>

        {/* Body */}
        <Flex
          flex="1"
          flexDir={{ base: 'column', md: 'row' }}
          minH="0"
        css={{
            '@media (min-width: 768px)': {
              display: 'grid',
              gridTemplateColumns: '1fr 340px',
              gridTemplateRows: '1fr',
              minH: '420px',
            },
          }}
        >
          {/* Map */}
          <Box
            h={{ base: '200px', md: '400px' }}
            m={{ base: '5', md: '0' }}
          css={{
              '@media (max-width: 425px)': { m: '4', h: '180px' },
            }}
            borderRadius={{ base: '2xl', md: '0' }}
            overflow="hidden"
            flexShrink={0}
            w={{ md: 'full' }}
            ref={mapRef}
            style={{ borderBottomLeftRadius: 'var(--chakra-radii-2xl)' }}
          />

          {/* Sidebar */}
          <Flex
            flex="1"
            flexDir="column"
            minH="0"
            overflowY="auto"
            borderLeft={{ md: '1px solid' }}
            borderColor={{ md: 'volyx.borderLight' }}
          >
            {/* Selected locations chips */}
            {selectedLocations.length > 0 && (
              <Box
                px={{ base: '5', md: '4' }}
                py="3"
                flexShrink={0}
                css={{
                  '@media (max-width: 425px)': { px: '4' },
                  '@media (min-width: 768px)': { pt: '4', pb: '2' },
                }}
              >
                <Text fontSize="md" fontWeight="semibold" color="volyx.text" mb="2" fontFamily="body">
                  Selected Locations ({selectedLocations.length})
                </Text>
                <Flex
                  gap="2"
                  overflowX="auto"
                  pb="1"
                  css={{
                    '&::-webkit-scrollbar': { h: '1' },
                    '&::-webkit-scrollbar-track': { bg: 'transparent' },
                    '&::-webkit-scrollbar-thumb': { bg: '#ccc', borderRadius: 'sm' },
                  }}
                >
                  {selectedLocations.map((location, index) => (
                    <Flex
                      key={`${location.name}-${index}`}
                      alignItems="center"
                      bg="volyx.lightGray"
                      borderRadius="full"
                      py="1.5"
                      px="3"
                      gap="2"
                      whiteSpace="nowrap"
                      flexShrink={0}
                    >
                      <Box color="volyx.primary" flexShrink={0}>
                        <MapPin size={14} />
                      </Box>
                      <Text fontSize="sm" fontFamily="body" color="volyx.text">
                        {location.name}
                      </Text>
                      <chakra.button
                        p="1"
                        bg="transparent"
                        border="none"
                        cursor="pointer"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        color="volyx.error"
                        transition="opacity 0.2s"
                        _hover={{ opacity: 0.7 }}
                        onClick={() => removeSelectedLocation(location)}
                      >
                        <X size={12} />
                      </chakra.button>
                    </Flex>
                  ))}
                </Flex>
              </Box>
            )}

            {/* Search + results */}
            <Flex
              flex="1"
              px={{ base: '5', md: '4' }}
              pb={{ base: '2', md: '4' }}
              overflowY="auto"
              flexDir="column"
              minH="0"
              css={{ '@media (max-width: 425px)': { px: '4' } }}
            >
              {/* Search bar */}
              <Flex alignItems="center" bg="volyx.lightGray" borderRadius="2xl" p="3" px="4" gap="3">
                <Box color="volyx.textSecondary" flexShrink={0}>
                  <Search size={20} />
                </Box>
                <chakra.input
                  type="text"
                  id="location-picker-search"
                  name="location-search"
                  flex="1"
                  fontSize="md"
                  fontFamily="body"
                  color="volyx.text"
                  bg="transparent"
                  border="none"
                  outline="none"
                  _placeholder={{ color: 'volyx.textSecondary' }}
                  placeholder="Search cities worldwide..."
                  value={searchQuery}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
                />
                {searchQuery.length > 0 && (
                  <chakra.button
                    onClick={() => setSearchQuery('')}
                    p="1"
                    bg="transparent"
                    border="none"
                    cursor="pointer"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    color="volyx.textSecondary"
                    transition="opacity 0.2s"
                    _hover={{ opacity: 0.7 }}
                  >
                    <X size={18} />
                  </chakra.button>
                )}
              </Flex>

              {/* Searching spinner */}
              {isSearching && (
                <Flex alignItems="center" justifyContent="center" py="5">
                  <Text fontSize="sm" fontFamily="body" color="volyx.textSecondary" textAlign="center">
                    Searching...
                  </Text>
                </Flex>
              )}

              {/* Global search results */}
              {!isSearching && (globalSearchResults as unknown[]).length > 0 && (
                <Box maxH="300px" bg="white" borderRadius="xl" mt="2" boxShadow="md" overflowY="auto">
                  {(globalSearchResults as unknown[]).map((result: unknown, index: number) => {
                    const r = result as Record<string, unknown>;
                    const placeId = (r.placePrediction as { placeId?: string } | undefined)?.placeId ?? (r.placeId as string) ?? (r.place_id as string) ?? `result-${index}`;
                    const descriptionStr = getDescriptionFromSuggestion(r, index);
                    return (
                      <chakra.button
                        key={placeId ?? `location-${index}`}
                        display="flex"
                        alignItems="center"
                        p="3"
                        px="4"
                        borderBottom="1px solid"
                        borderColor="volyx.borderLight"
                        w="full"
                        textAlign="left"
                        cursor="pointer"
                        transition="colors 0.2s"
                        _hover={{ bg: '#f9fafb' }}
                        _last={{ borderBottom: '0' }}
                        bg="transparent"
                        border="none"
                        css={{ borderBottom: '1px solid', borderColor: 'volyx.borderLight', '&:last-child': { borderBottom: '0' } }}
                        onClick={() => handleGlobalLocationSelect(placeId, descriptionStr)}
                      >
                        <Box color="volyx.primary" mr="3" flexShrink={0}>
                          <MapPin size={16} />
                        </Box>
                        <Text fontSize="sm" fontFamily="body" color="volyx.text" flex="1">
                          {descriptionStr}
                        </Text>
                      </chakra.button>
                    );
                  })}
                </Box>
              )}

              {/* No results */}
              {!isSearching && (globalSearchResults as unknown[]).length === 0 && searchQuery.length > 0 && (
                <Flex flexDir="column" alignItems="center" justifyContent="center" py="60px" px="5">
                  <Text as="h4" fontSize="lg" fontWeight="semibold" color="volyx.text" textAlign="center" mb="2" fontFamily="body">
                    No locations found
                  </Text>
                  <Text fontSize="sm" fontFamily="body" color="volyx.textSecondary" textAlign="center" lineHeight="5">
                    Try searching for a different city or location
                  </Text>
                </Flex>
              )}

              {/* Top opportunity locations */}
              {!isSearching && (globalSearchResults as unknown[]).length === 0 && searchQuery.length === 0 && selectedLocations.length === 0 && (
                <Box pt="2.5" mt="2">
                  <Flex alignItems="center" mb="2">
                    <Box color="volyx.primary" mr="2">
                      <TrendingUp size={20} />
                    </Box>
                    <Text as="h4" fontSize="md" fontWeight="semibold" color="volyx.text" m="0" fontFamily="body">
                      Top Opportunity Locations
                    </Text>
                  </Flex>
                  <Text fontSize="sm" fontFamily="body" color="volyx.textSecondary" mb="3">
                    Cities with the most volunteer opportunities
                  </Text>
                  {topOpportunityLocations.map((location, index) => {
                    const isSelected = selectedLocations.some((loc) => loc.name === location.name);
                    return (
                      <chakra.button
                        key={location.name}
                        position="relative"
                        display="flex"
                        flexDir="column"
                        w="full"
                        textAlign="left"
                        cursor="pointer"
                        bg="volyx.lightGray"
                        borderRadius="xl"
                        p="3"
                        mb="3"
                        border={isSelected ? '2px solid' : '1px solid'}
                        borderColor={isSelected ? 'volyx.primary' : 'volyx.lightGray'}
                        transition="colors 0.2s"
                        _hover={{ borderColor: 'volyx.primary' }}
                        onClick={() => handleOpportunityLocationSelect(location)}
                      >
                        <Flex alignItems="center" mb="1">
                          <Flex
                            bg="volyx.primary"
                            borderRadius="10px"
                            w="6"
                            h="6"
                            alignItems="center"
                            justifyContent="center"
                            mr="2.5"
                            flexShrink={0}
                            color="white"
                            fontSize="xs"
                            fontWeight="bold"
                            fontFamily="body"
                          >
                            #{index + 1}
                          </Flex>
                          <Flex flex="1" mr="2.5" flexDir="column">
                            <Text fontSize="md" fontWeight="semibold" color="volyx.text" fontFamily="body">
                              {location.name}
                            </Text>
                            <Text fontSize="sm" fontFamily="body" color="volyx.textSecondary">
                              {location.region}
                            </Text>
                          </Flex>
                          <Flex alignItems="center" mr="4">
                            <Box color="green.500" mr="1">
                              <Calendar size={14} />
                            </Box>
                            <Text fontSize="sm" fontWeight="medium" color="volyx.text" fontFamily="body">
                              {location.activeOpportunities}
                            </Text>
                          </Flex>
                        </Flex>
                        {isSelected && (
                          <Flex
                            position="absolute"
                            right="2.5"
                            top="2.5"
                            bg="volyx.primary"
                            borderRadius="10px"
                            p="1"
                            color="white"
                          >
                            <MapPin size={16} />
                          </Flex>
                        )}
                      </chakra.button>
                    );
                  })}
                </Box>
              )}
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
}
